import {
    LIST_SELECT, LIST_DESELECT, LIST_SELECT_BY_GROUP,
    LIST_DESELECT_BY_GROUP, LIST_HIGHLIGHT, LIST_UNHIGHLIGHT, LIST_SELECTION_MODE_SET
} from '../../action-types';
import { createReducer, isMatchingByFilters } from '../../../utils/state';
import { ListItem } from '../../../models/common/list-item';
import { initialListState } from './list.state';
import { State as ListFilterState } from './list-filter.reducer';
import { SelectionMode } from '@core/models/common/selection-mode';

export const listSelection = createReducer(initialListState, {

    /**
     * Select items by ids.
     * @param state
     * @param payload
     *
     */
    [LIST_SELECT]: (state, { payload }, entities?: any) => {
        let selectedIds: string[];

        // if multiple is false the selectedIds will hold only one value at a time
        if (payload.multiple === false) {
            selectedIds = [payload.ids[0]];
        } else {
            const existingSelectedIds: string[] = state.selectedIds;
            selectedIds = payload.ids
                .filter((id) => existingSelectedIds.indexOf(id) < 0)
                .concat(existingSelectedIds);
        }

        let nextState: any = { ...state, selectedIds };

        // if there is some selectionMode set, and all ids have been selected then the mode becomes ALL_IN_VIEW
        if (state.ids && selectedIds.length === state.ids.length) {
            if (state.meta) {
                if (!state.meta.has_next) {
                    nextState.selectionMode = SelectionMode.ALL_IN_VIEW;
                }
            } else {
                nextState.selectionMode = SelectionMode.ALL_IN_VIEW;
            }

        }

        return nextState;
    },

    /**
     * Deselect items by ids.
     * Auto deselect a group if it includes a deselected id.
     * @param state
     * @param payload
     * @param entities Used to validate entity group
     *
     */
    [LIST_DESELECT]: (state, { payload }, entities?: any) => {
        // Update selected ids
        const existingSelectedGroups: string[] = state.selectedGroups;
        let selectedIds: string[];
        let selectedGroups: string[];

        if (payload.ids) {
            const deselectIds: string[] = payload.ids;
            selectedIds = state.selectedIds
              .filter((id) => deselectIds.indexOf(id) < 0);

            // Deselect any groups that match a deselected item
            selectedGroups = existingSelectedGroups.concat();
            if (entities && payload.domain) {

                // Define groups from deselected items
                const deselectedGroups: any[] = deselectIds
                  .map((id) => entities[payload.domain][id])
                  .map((item) => (item as ListItem).getGroup(state.groupBy));

                selectedGroups = existingSelectedGroups
                  .filter((selectedGroup) => deselectedGroups.indexOf(selectedGroup) < 0);
            }
        } else {

            // Deselect all when no ids defined
            selectedIds = [];
            selectedGroups = [];
        }

        return { ...state, selectedIds, selectedGroups, selectionMode: null };
    },

    /**
     * Highlight items by ids.
     * @param state
     * @param payload
     *
     */
    [LIST_HIGHLIGHT]: (state, { payload }) => {
        let highlightIds: string[];

        // if multiple is false the highlightIds will hold only one value at a time
        if (payload.multiple === false) {
            highlightIds = [payload.ids[0]];
        } else {
            const existingHighlightIds: string[] = state.highlightIds;
            highlightIds = payload.ids
                .filter((id) => existingHighlightIds.indexOf(id) < 0)
                .concat(existingHighlightIds);
        }

        return { ...state, highlightIds };
    },

    /**
     * Unhighlight items by ids.
     * @param state
     * @param payload
     *
     */
    [LIST_UNHIGHLIGHT]: (state, { payload }) => {
        // Update highlight ids
        const deselectIds: string[] = payload.ids;
        const highlightIds: string[] = state.highlightIds
            .filter((id) => deselectIds.indexOf(id) < 0);

        return { ...state, highlightIds };
    },

    /**
     * Select items matching group.
     * Track group and update selected ids.
     * @param state
     * @param payload
     * @param entities Used to match entity group
     *
     */
    [LIST_SELECT_BY_GROUP]: (state, { payload }, entities?: any) => {
        const updatedGroups: string[] = payload.groups;
        const existingSelectedGroups: string[] = state.selectedGroups;
        const existingSelectedIds: string[] = state.selectedIds;

        // Ensure these groups are selected with no duplicates
        const selectedGroups: string[] = updatedGroups
            .filter((selectedDate) => existingSelectedGroups.indexOf(selectedDate) < 0)
            .concat(existingSelectedGroups)
            .filter((group, index, groupItems) => groupItems.indexOf(group) === index);

        // Define items that match group & within applied filters
        const includeSelectedIds: string[] = [];
        const filters: ListFilterState = state.filterBy;
        if (entities && payload.domain) {
            state.ids.forEach((key) => {
                let isMatching: boolean = false;
                let item: ListItem = entities[payload.domain][key] as ListItem;

                // Include items matching group
                if (item.isMatchingGroup(state.groupBy, updatedGroups)) {
                    isMatching = true;
                }

                // Include items matching filters
                if (isMatching) {
                    isMatching = isMatchingByFilters(filters, isMatching, item);
                }

                if (isMatching) {
                    includeSelectedIds.push(item.id);
                }
            });
        }

        // Include selected ids of items matching group
        const selectedIds: string[] = includeSelectedIds
            .filter((itemId) => existingSelectedIds.indexOf(itemId) < 0)
            .concat(existingSelectedIds);

        return { ...state, selectedIds, selectedGroups };
    },

    /**
     * Deselect items by group.
     * @param state
     * @param payload
     * @param entities Used to validate entity group
     */
    [LIST_DESELECT_BY_GROUP]: (state, { payload }, entities?: any) => {
        const deselectGroups: string[] = payload.groups;
        const existingSelectedGroups: string[] = state.selectedGroups;
        const existingSelectedIds: string[] = state.selectedIds;

        // Ensure these groups are deselected
        const selectedGroups: string[] = existingSelectedGroups
            .filter((selectedDate) => deselectGroups.indexOf(selectedDate) < 0);

        // Define items that match group to deselect
        let deselectedIds: any[] = [];
        if (entities && payload.domain) {
            deselectedIds = existingSelectedIds
                .map((id) => entities[payload.domain][id] as ListItem)
                .filter((item: ListItem) => item.isMatchingGroup(state.groupBy, deselectGroups))
                .map((item: ListItem) => item.id);
        }

        // Ensure we deselect matching item ids
        const selectedIds = existingSelectedIds
            .filter((itemId) => deselectedIds.indexOf(itemId) < 0);

        return { ...state, selectedIds, selectedGroups };
    },

    [LIST_SELECTION_MODE_SET]: (state, { payload }) => {
        return {
            ...state,
            selectionMode: payload.mode
        }
    }
});
